import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../contexts/NotificationContext';
import type { User } from '../types/auth';
import type { Simulation, Topology } from '../types/simulation';
import {
  getAllUsers,
  updateUser,
  deleteUser,
  getAllSimulationsAdmin,
  getAllTopologiesAdmin,
  updateSimulation,
  deleteSimulation,
  publishTopology,
  deleteTopology,
  createAIUser,
  getUserAPIKeys,
} from '../services/api';
import './AdminPanelPage.css';

type TabType = 'users' | 'simulations' | 'topologies';

interface AdminPanelPageProps {
  onBack: () => void;
  setTitle: (title: string) => void;
}

export function AdminPanelPage({ onBack, setTitle }: AdminPanelPageProps) {
  const { user } = useAuth();
  const { showNotification } = useNotification();

  useEffect(() => {
    setTitle('ADMIN PANEL');
  }, [setTitle]);
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [topologies, setTopologies] = useState<Topology[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editingUserRoles, setEditingUserRoles] = useState<string[]>([]);
  const [showCreateAIUser, setShowCreateAIUser] = useState(false);
  const [aiUsername, setAiUsername] = useState('');
  const [aiDisplayName, setAiDisplayName] = useState('');
  const [createdAPIKey, setCreatedAPIKey] = useState<string | null>(null);

  // Redirect if not admin
  useEffect(() => {
    if (user && !user.roles.includes('admin')) {
      onBack();
    }
  }, [user, onBack]);

  // Load data when tab changes
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'users') {
        const data = await getAllUsers();
        setUsers(data);
      } else if (activeTab === 'simulations') {
        const data = await getAllSimulationsAdmin();
        setSimulations(data);
      } else if (activeTab === 'topologies') {
        const data = await getAllTopologiesAdmin();
        setTopologies(data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRoles = async (userId: number, roles: string[]) => {
    try {
      await updateUser(userId, { roles });
      await loadData();
      setEditingUserId(null);
    } catch (err: any) {
      alert(err.message || 'Failed to update user');
    }
  };

  const toggleRole = (roleName: string) => {
    setEditingUserRoles(prev =>
      prev.includes(roleName)
        ? prev.filter(r => r !== roleName)
        : [...prev, roleName]
    );
  };

  const handleDeleteUser = async (userId: number, username: string) => {
    if (!confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try {
      await deleteUser(userId);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to delete user');
    }
  };

  const handleDeleteSimulation = async (simId: string, simName: string) => {
    if (!confirm(`Delete simulation "${simName}"? This cannot be undone.`)) return;
    try {
      await deleteSimulation(simId);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to delete simulation');
    }
  };

  const handleToggleSimulationStatus = async (sim: Simulation) => {
    const newStatus = sim.status === 'active' ? 'maintenance' : 'active';
    try {
      await updateSimulation(sim.id, { status: newStatus });
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to update simulation');
    }
  };

  const handlePublishTopology = async (topoId: string) => {
    try {
      await publishTopology(topoId);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to publish topology');
    }
  };

  const handleDeleteTopology = async (topoId: string, topoName: string) => {
    if (!confirm(`Delete topology "${topoName}"? This cannot be undone.`)) return;
    try {
      await deleteTopology(topoId);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to delete topology');
    }
  };

  const handleCreateAIUser = async () => {
    if (!aiUsername.trim()) {
      alert('Username is required');
      return;
    }
    try {
      const result = await createAIUser({
        username: aiUsername,
        display_name: aiDisplayName || undefined,
      });
      setCreatedAPIKey((result as any).api_key);
      setAiUsername('');
      setAiDisplayName('');
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to create AI user');
    }
  };

  const handleCloseAPIKeyModal = () => {
    setCreatedAPIKey(null);
    setShowCreateAIUser(false);
  };

  const handleCopyAPIKey = async (userId: number, username: string) => {
    try {
      const apiKeys = await getUserAPIKeys(userId);
      const activeKey = apiKeys.find(key => !key.revoked);

      if (!activeKey) {
        alert(`No active API key found for user "${username}"`);
        return;
      }

      await navigator.clipboard.writeText(activeKey.key);
      showNotification('API key copied to clipboard!');
    } catch (err: any) {
      alert(err.message || 'Failed to retrieve API key');
    }
  };

  // Create portal content for toolbar
  const portalTarget = document.getElementById('global-toolbar-portal');
  const toolbarContent = portalTarget ? ReactDOM.createPortal(
    <>
      <div className="toolbar-left">
        <button className="back-button" onClick={onBack} title="Back to dashboard">
          &lt; BACK
        </button>
      </div>
      {activeTab === 'users' && (
        <div className="toolbar-right">
          <div
            className="toolbar-link toolbar-link--primary"
            onClick={() => setShowCreateAIUser(true)}
            title="Create AI User"
          >
            + create ai user
          </div>
        </div>
      )}
    </>,
    portalTarget
  ) : null;

  return (
    <div className="app">
      {toolbarContent}

      <div className="admin-panel-page">
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            USERS
          </button>
          <button
            className={`admin-tab ${activeTab === 'simulations' ? 'active' : ''}`}
            onClick={() => setActiveTab('simulations')}
          >
            SIMULATIONS
          </button>
          <button
            className={`admin-tab ${activeTab === 'topologies' ? 'active' : ''}`}
            onClick={() => setActiveTab('topologies')}
          >
            TOPOLOGIES
          </button>
        </div>

        <div className="admin-content">
          {loading && (
            <div className="loading">
              <div className="loading-spinner"></div>
              <p>&gt; LOADING...</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <p>ERROR: {error}</p>
              <button onClick={loadData}>RETRY</button>
            </div>
          )}

          {!loading && !error && activeTab === 'users' && (
            <div className="admin-users">
              <h2>User Management ({users.length} users)</h2>
              <div className="admin-table">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Roles</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>{u.username}</td>
                        <td>{u.email}</td>
                        <td>
                          {editingUserId === u.id ? (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <input
                                  type="checkbox"
                                  checked={editingUserRoles.includes('user')}
                                  onChange={() => toggleRole('user')}
                                />
                                user
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <input
                                  type="checkbox"
                                  checked={editingUserRoles.includes('admin')}
                                  onChange={() => toggleRole('admin')}
                                />
                                admin
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <input
                                  type="checkbox"
                                  checked={editingUserRoles.includes('AI')}
                                  onChange={() => toggleRole('AI')}
                                />
                                AI
                              </label>
                            </div>
                          ) : (
                            <span>{u.roles.map(r => (
                              <span key={r} className={`role-badge role-${r}`} style={{ marginRight: '4px' }}>
                                {r}
                              </span>
                            ))}</span>
                          )}
                        </td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        <td>
                          {editingUserId === u.id ? (
                            <>
                              <button
                                className="btn-small btn-primary"
                                onClick={() => handleUpdateUserRoles(u.id, editingUserRoles)}
                              >
                                Save
                              </button>
                              <button
                                className="btn-small btn-secondary"
                                onClick={() => setEditingUserId(null)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="btn-small btn-secondary"
                                onClick={() => {
                                  setEditingUserId(u.id);
                                  setEditingUserRoles([...u.roles]);
                                }}
                                disabled={u.id === user?.id}
                              >
                                Edit Roles
                              </button>
                              <button
                                className="btn-small btn-danger"
                                onClick={() => handleDeleteUser(u.id, u.username)}
                                disabled={u.id === user?.id}
                              >
                                Delete
                              </button>
                              {u.roles.includes('AI') && (
                                <button
                                  className="btn-small btn-primary"
                                  onClick={() => handleCopyAPIKey(u.id, u.username)}
                                  title="Copy API key to clipboard"
                                >
                                  Copy API Key
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && !error && activeTab === 'simulations' && (
            <div className="admin-simulations">
              <h2>Simulation Management ({simulations.length} simulations)</h2>
              <div className="admin-table">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Private</th>
                      <th>AI Models</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simulations.map((sim) => (
                      <tr key={sim.id}>
                        <td>{sim.id.substring(0, 8)}...</td>
                        <td>{sim.name}</td>
                        <td>
                          <span className={`status-badge status-${sim.status}`}>
                            {sim.status}
                          </span>
                        </td>
                        <td>{sim.is_private ? 'Yes' : 'No'}</td>
                        <td>{sim.ai_models?.length || 0}</td>
                        <td>{sim.created_at ? new Date(sim.created_at).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <button
                            className="btn-small btn-secondary"
                            onClick={() => handleToggleSimulationStatus(sim)}
                          >
                            {sim.status === 'active' ? 'Set Maintenance' : 'Set Active'}
                          </button>
                          <button
                            className="btn-small btn-danger"
                            onClick={() => handleDeleteSimulation(sim.id, sim.name)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && !error && activeTab === 'topologies' && (
            <div className="admin-topologies">
              <h2>Topology Management ({topologies.length} topologies)</h2>
              <div className="admin-table">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>File Path</th>
                      <th>Published</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topologies.map((topo) => (
                      <tr key={topo.id}>
                        <td>{topo.id.substring(0, 8)}...</td>
                        <td>{topo.name}</td>
                        <td>{topo.file_path}</td>
                        <td>
                          <span className={`publish-badge ${topo.published ? 'published' : 'unpublished'}`}>
                            {topo.published ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>{new Date(topo.created_at).toLocaleDateString()}</td>
                        <td>
                          {!topo.published && (
                            <button
                              className="btn-small btn-primary"
                              onClick={() => handlePublishTopology(topo.id)}
                            >
                              Publish
                            </button>
                          )}
                          <button
                            className="btn-small btn-danger"
                            onClick={() => handleDeleteTopology(topo.id, topo.name)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Create AI User Modal */}
        {showCreateAIUser && !createdAPIKey && (
          <div className="modal-overlay" onClick={() => setShowCreateAIUser(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Create AI User</h2>
              <p className="modal-description">
                AI users cannot log in via the UI and use ws_token for authentication.
              </p>
              <div className="form-group">
                <label>Username (required):</label>
                <input
                  type="text"
                  value={aiUsername}
                  onChange={(e) => setAiUsername(e.target.value)}
                  placeholder="ai_agent_1"
                />
              </div>
              <div className="form-group">
                <label>Display Name (optional):</label>
                <input
                  type="text"
                  value={aiDisplayName}
                  onChange={(e) => setAiDisplayName(e.target.value)}
                  placeholder="AI Agent 1"
                />
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowCreateAIUser(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleCreateAIUser}>
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* API Key Display Modal */}
        {createdAPIKey && (
          <div className="modal-overlay" onClick={handleCloseAPIKeyModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <h2>AI User Created Successfully</h2>
              <p className="modal-description">
                Save this API key securely. The AI user will use it to retrieve their ws_token.
              </p>

              <div className="form-group">
                <label>API Key:</label>
                <textarea
                  className="token-textarea"
                  readOnly
                  value={createdAPIKey}
                  onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                />
                <button
                  className="btn-small btn-secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(createdAPIKey);
                    showNotification('API key copied!');
                  }}
                  style={{ marginTop: '8px' }}
                >
                  Copy API Key
                </button>
              </div>

              <div style={{
                background: 'rgba(100, 100, 100, 0.1)',
                padding: '12px',
                borderRadius: '4px',
                marginTop: '1rem',
                fontSize: '0.85rem',
                color: '#888',
                fontFamily: 'monospace'
              }}>
                <strong>To retrieve ws_token:</strong>
                <br />
                <code>POST /api/auth/ai-auth</code>
                <br />
                <code>Body: {`{ "username": "...", "api_key": "..." }`}</code>
                <br />
                <code>Returns: {`{ "ws_token": "..." }`}</code>
              </div>

              <div className="modal-actions">
                <button className="btn-primary" onClick={handleCloseAPIKeyModal}>
                  I've Saved This API Key
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
